import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import FleetWidget from 'models/fleet-management/FleetWidget';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector } from 'store';
import { selectFleetDataSetsData } from 'store/fleet-management/fleet-management-slice-selectors';
import { FleetWidgetType } from 'models/enums/fleet-management';
import { ResizeIcon } from '@common/utils/icons/icons';

enum DragPoint {
  TOP_LEFT,
  BOTTOM_RIGHT,
}

const WidgetEditGrid: React.FC<{
  widgets: FleetWidget[];
  unitWidth: number;
  selectedWidget: { id: string | null; onChange: (id: string | null) => void };
  onChange: (updatedWidgets: FleetWidget[]) => void;
}> = ({
  widgets,
  unitWidth,
  selectedWidget: { id: selectedWidgetId, onChange: setSelectedWidgetId },
  onChange,
}) => {
  const datasets = useAppSelector((state) => selectFleetDataSetsData(state));

  const [dragPoint, setDragPoint] = useState<DragPoint | null>(null);

  const selectedWidget = widgets.find((w) => w.id === selectedWidgetId);

  useEffect(() => {
    const mouseUpTouchEndHandler = () => {
      setDragPoint(null);
    };

    window.addEventListener('mouseup', mouseUpTouchEndHandler);
    window.addEventListener('touchend', mouseUpTouchEndHandler);

    return () => {
      window.removeEventListener('mouseup', mouseUpTouchEndHandler);
      window.removeEventListener('touchend', mouseUpTouchEndHandler);
    };
  }, []);

  const getWidget = (row: number, col: number) => {
    return widgets.find(
      (w) =>
        w.grid != null &&
        w.grid.length === 2 &&
        w.grid[0][1] <= row &&
        w.grid[0][0] <= col &&
        w.grid[1][1] > row &&
        w.grid[1][0] > col,
    );
  };

  const updateWidgetHandler = (update: Partial<FleetWidget>) => {
    if (datasets == null || datasets?.length === 0) return;

    const currentWidget: FleetWidget = widgets.find(
      (w) => w.id === (update.id ?? selectedWidgetId),
    ) ?? {
      id: '',
      fields: [],
      name: '',
      groups: null,
      type: FleetWidgetType.TEXT,
      grid: [
        [0, 0],
        [1, 1],
      ],
      dataset: datasets.find((ds) => ds.id === selectedWidget?.dataset)?.id ?? datasets[0].id,
      description: null,
      filters: [],
      limit: null,
      orderBy: null,
      bucketField: null,
      bucket: null,
    };
    onChange([
      ...widgets.filter((w) => w.id !== currentWidget.id),
      { ...currentWidget, ...update },
    ]);
  };

  const onWidgetClickHandler = (id: string | null, row: number, col: number) => {
    const widgetId = id ?? uuidv4();
    setSelectedWidgetId(widgetId);
    if (widgetId !== id) {
      updateWidgetHandler({
        id: widgetId,
        grid: [
          [col, row],
          [col + 1, row + 1],
        ],
      });
    }
  };

  const onDragPointMouseDown = (dp: DragPoint) => {
    setDragPoint(dp);
  };

  const boxMouseOverHandler = (row: number, col: number) => {
    if (dragPoint == null || selectedWidget == null) return;

    const existingGridPoints = selectedWidget.grid ?? [
      [0, 0],
      [1, 1],
    ];

    const widgetsOverlap = (a: number[][], b: number[][]): boolean => {
      const [a1, a2] = a;
      const [b1, b2] = b;

      const ax1 = a1[0],
        ay1 = a1[1];
      const ax2 = a2[0],
        ay2 = a2[1];

      const bx1 = b1[0],
        by1 = b1[1];
      const bx2 = b2[0],
        by2 = b2[1];

      // Check interval overlap on both axes (exclusive — touching edges is not overlap)
      const xOverlap = ax1 < bx2 && ax2 > bx1;
      const yOverlap = ay1 < by2 && ay2 > by1;

      return xOverlap && yOverlap;
    };

    const newGridPoints =
      dragPoint === DragPoint.TOP_LEFT
        ? [[col, row], existingGridPoints[1]]
        : [existingGridPoints[0], [col + 1, row + 1]];

    if (newGridPoints[0][0] >= newGridPoints[1][0] || newGridPoints[0][1] >= newGridPoints[1][1])
      return;

    if (
      !widgets
        .filter((w) => w.id !== selectedWidget.id && w.grid != null)
        .some((w) => widgetsOverlap(newGridPoints, w.grid!))
    ) {
      updateWidgetHandler({ grid: newGridPoints });
    }
  };

  const bottomWidget = widgets.reduce(
    (min, w) => (min = w.grid != null && w.grid.length === 2 ? Math.max(min, w.grid[1][1]) : 1),
    0,
  );

  return (
    <React.Fragment>
      {Array.from(Array(bottomWidget + 1).keys()).map((row) => (
        <Stack key={row} direction="row" alignItems="center" width="100%">
          {Array.from(Array(12).keys()).map((col) => {
            const widget = getWidget(row, col);

            return (
              <Box
                onMouseOver={() => {
                  boxMouseOverHandler(row, col);
                }}
                onClick={() => onWidgetClickHandler(widget?.id ?? null, row, col)}
                key={`${row}_${col}`}
                sx={(theme) => ({
                  position: 'relative',
                  width: unitWidth,
                  height: unitWidth,
                  pointerEvents: selectedWidgetId == null && widget != null ? 'none' : undefined,
                  cursor: 'pointer',
                  padding: 0.5,
                })}
              >
                {widget == null && (
                  <Box
                    sx={(theme) => ({
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'lightgray',
                      borderRadius: 1,
                      border: '1px solid grey',
                      borderStyle: 'dashed',
                      ':hover': { backgroundColor: theme.palette.action.hover },
                    })}
                  />
                )}
                {widget != null &&
                  widget.id === selectedWidgetId &&
                  widget.grid != null &&
                  widget.grid.length > 1 &&
                  widget.grid[0][0] === col &&
                  widget.grid[0][1] === row && (
                    <IconButton
                      onMouseDown={() => onDragPointMouseDown(DragPoint.TOP_LEFT)}
                      color="primary"
                      size="large"
                      sx={{
                        position: 'absolute',
                        cursor: 'nwse-resize',
                        top: '-8px',
                        left: '-8px',
                        zIndex: 1,
                      }}
                    >
                      <ResizeIcon fontSize="large" sx={{ transform: 'rotate(135deg)' }} />
                    </IconButton>
                  )}
                {widget != null &&
                  widget.id === selectedWidgetId &&
                  widget.grid != null &&
                  widget.grid.length > 1 &&
                  widget.grid[1][0] === col + 1 &&
                  widget.grid[1][1] === row + 1 && (
                    <IconButton
                      onMouseDown={() => onDragPointMouseDown(DragPoint.BOTTOM_RIGHT)}
                      color="primary"
                      size="large"
                      sx={{
                        position: 'absolute',
                        cursor: 'nwse-resize',
                        bottom: '-8px',
                        right: '-8px',
                        zIndex: 1,
                      }}
                    >
                      <ResizeIcon fontSize="large" sx={{ transform: 'rotate(-45deg)' }} />
                    </IconButton>
                  )}
              </Box>
            );
          })}
        </Stack>
      ))}
    </React.Fragment>
  );
};

export default WidgetEditGrid;
